import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { AnnotationContext } from '@cloudscape-design/components';
import { useNavigate, useLocation } from 'react-router-dom';

/*
 * TutorialContext
 *
 * Drives the Cloudscape AnnotationContext + Hotspot guided-tour experience
 * so the app can run as a self-driven demo when the booth is unattended.
 *
 * Each tutorial is a list of tasks; each task is a list of steps pointing
 * at a `hotspotId` which is placed next to the relevant UI element via
 * `<Hotspot hotspotId="..." />`.
 *
 * Steps can request a route change before they appear by setting a
 * `navigateTo` field (string path). The provider listens for step
 * changes and pushes the route so a tutorial can span pages.
 */

// ---- Tutorial definitions ---------------------------------------------

export const HOTSPOT_IDS = {
  // Home page
  homeBeginAnalysis: 'home-begin-analysis',
  homeCurrentProcess: 'home-current-process',
  homeSaraWorkflow: 'home-sara-workflow',
  homePersonaSara: 'home-persona-sara',
  homeShowTutorial: 'home-show-tutorial',

  // v2 Process page
  processReady: 'process-ready-alert',
  processDocAnnual: 'process-doc-annual-2024',
  processDocQuarterly: 'process-doc-quarterly-2025',
  processQuestions: 'process-research-questions',
  processDocument: 'process-document-viewer',
  processPipeline: 'process-agent-pipeline',
  processStart: 'process-start-button',

  // v2 Review page
  reviewAnswers: 'review-answers',
  reviewInsightsTab: 'review-insights-tab',
  reviewReport: 'review-generate-report',
  reviewWhatsNextTab: 'review-whats-next-tab',
};

/**
 * Build the single guided tour that spans Home → Process → Review.
 * The tour automatically navigates between pages at the right moment
 * by using `onStepChange` in the provider.
 */
export const DEMO_TUTORIAL = {
  title: 'ResearchIQ Guided Demo',
  description: 'A self-guided walkthrough of the ResearchIQ experience.',
  completedScreenDescription: 'You have completed the ResearchIQ guided demo.',
  prerequisitesAlert: null,
  completed: false,
  tasks: [
    {
      title: 'Step 1 — Home',
      stepsCount: 3,
      steps: [
        {
          title: 'Welcome to ResearchIQ',
          content: (
            <p>
              Here is our Financial Research Assistant — an AI-powered
              investment research platform that transforms how analysts
              conduct questionnaire-based document analysis using Amazon
              Bedrock and serverless architecture.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.homeBeginAnalysis,
          navigateTo: '/',
        },
        {
          title: 'The traditional research burden',
          content: (
            <p>
              In traditional investment research, your analysts spend hours
              manually combing through multiple document types, looking for
              answers to the same fundamental questions they ask about
              every potential investment.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.homeCurrentProcess,
          navigateTo: '/',
        },
        {
          title: "Experience Sara's workflow",
          content: (
            <p>
              Our Financial Research Assistant standardises these critical
              investment questions and automates the entire research
              process. Let's experience Sara's workflow by clicking the
              button.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.homeSaraWorkflow,
          navigateTo: '/',
        },
      ],
    },
    {
      title: 'Step 2 — Process',
      stepsCount: 6,
      steps: [
        {
          title: 'Ready for AI analysis',
          content: (
            <p>
              Sara is ready for AI analysis with her 4 documents and 6
              research questions for an in depth look at the company
              profile.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.processReady,
          navigateTo: '/v2/process',
        },
        {
          title: 'Amazon Annual Report 2024',
          content: (
            <p>
              She has 4 documents she's found for her research. The Amazon
              Annual Report 2024 has 91 pages of unstructured data, which
              is a long read. Click <strong>View</strong> to see the
              document.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.processDocAnnual,
          navigateTo: '/v2/process',
        },
        {
          title: 'Amazon Q1 2025 Report',
          content: (
            <p>
              View the Amazon Q1 2025 Report — it shows that there is also
              formatted financial information in tables and charts here.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.processDocQuarterly,
          navigateTo: '/v2/process',
        },
        {
          title: 'Sara\'s research questions',
          content: (
            <p>
              For demo purposes we have limited it to 6 questions, but this
              could be many more asked in different methods. She has a
              broad range of question topics — revenue, competitive threats,
              key risk factors, growth, ESG, and expenditure &amp;
              investment.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.processQuestions,
          navigateTo: '/v2/process',
        },
        {
          title: 'Agent pipeline',
          content: (
            <>
              <p>
                With the analysis kicked off, all of the questions that
                Sara has asked will be passed to our 3 specialist agents to
                be researched in parallel:
              </p>
              <ul>
                <li>
                  <strong>Document Research Agent</strong> takes each of
                  Sara's questions and looks through the Knowledge Base
                  that contains the Amazon financial documents.
                </li>
                <li>
                  <strong>Evidence Compiler Agent</strong> analyses the
                  research findings for the question, looking for any
                  patterns, gaps or themes in the data that it can group
                  together to provide deeper insights.
                </li>
                <li>
                  <strong>Citation Tracker Agent</strong> creates a
                  polished, markdown-formatted research summary for each
                  question.
                </li>
              </ul>
            </>
          ),
          hotspotId: HOTSPOT_IDS.processPipeline,
          navigateTo: '/v2/process',
        },
        {
          title: 'Start the analysis',
          content: (
            <p>
              Tap <strong>Start Analysis</strong> to kick off the pipeline.
              Once the agents have completed, it will jump into the
              findings.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.processStart,
          navigateTo: '/v2/process',
        },
      ],
    },
    {
      title: 'Step 3 — Review',
      stepsCount: 4,
      steps: [
        {
          title: 'Research results',
          content: (
            <p>
              Here are the answers to Sara's six research questions. Each
              answer is grounded in the source documents with citations she
              can click through to verify. Agents can be instructed to
              output in a specific writing style or formatting.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.reviewAnswers,
          navigateTo: '/v2/review',
        },
        {
          title: 'AI strategic insights',
          content: (
            <p>
              From here Sara can jump into{' '}
              <strong>AI Strategic Insights</strong> to see cross-document
              trends, comparisons and recommended next steps distilled from
              her research.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.reviewInsightsTab,
          navigateTo: '/v2/review',
        },
        {
          title: 'Generate Executive Brief',
          content: (
            <p>
              Agents can prepare a summary briefing tailored to the reader,
              with fully customisable outputs to meet requirements.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.reviewReport,
          navigateTo: '/v2/review',
        },
        {
          title: "What's next",
          content: (
            <p>
              We've just shown you 6 questions on one company. You could
              scale this to many questions and many companies in parallel
              runs, or build out more analyst experiences for Sara. Have a
              look at the possibilities.
            </p>
          ),
          hotspotId: HOTSPOT_IDS.reviewWhatsNextTab,
          navigateTo: '/v2/review',
        },
      ],
    },
  ],
};

// ---- Provider ---------------------------------------------------------

const TutorialContext = createContext(null);

const I18N_STRINGS = {
  nextButtonText: 'Next',
  previousButtonText: 'Previous',
  finishButtonText: 'Finish',
  labelDismissAnnotation: 'Dismiss',
  labelHotspot: (openState, stepIndex, totalStepCount) =>
    openState
      ? `Close annotation for step ${stepIndex + 1} of ${totalStepCount}`
      : `Open annotation for step ${stepIndex + 1} of ${totalStepCount}`,
  stepCounterText: (stepIndex, totalStepCount) =>
    `Step ${stepIndex + 1} of ${totalStepCount}`,
  taskTitle: (taskIndex, taskTitle) => `Task ${taskIndex + 1}: ${taskTitle}`,
};

export function TutorialProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTutorial, setCurrentTutorial] = useState(null);

  const startTutorial = useCallback(() => {
    setCurrentTutorial(DEMO_TUTORIAL);
    // Jump back to the home page so the tour always starts from the top.
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [navigate, location.pathname]);

  const exitTutorial = useCallback(() => {
    setCurrentTutorial(null);
  }, []);

  // Look up the flat step list so we can find the step the user is now on
  // and, if that step requests a `navigateTo` path, push the route.
  const flatSteps = useMemo(() => {
    if (!currentTutorial) return [];
    return currentTutorial.tasks.flatMap((t) => t.steps);
  }, [currentTutorial]);

  const handleStepChange = useCallback(
    ({ detail }) => {
      const step = flatSteps[detail.step];
      if (!step || !step.navigateTo || step.navigateTo === location.pathname) {
        return;
      }

      // Only programmatically navigate on backward moves (Previous button) or
      // on auto-fallback (when the current hotspot disappears and the nearest
      // one happens to live on a different page).
      //
      // Forward moves (Next button) must NOT push the route — doing so would
      // jump the user past in-app flows that require user interaction (e.g.
      // clicking "Start Analysis" and waiting for the pipeline to complete
      // before the Review page has results to display). Let the app drive
      // forward navigation; Cloudscape's auto-fallback will pick up the next
      // hotspot once it mounts on the new page.
      if (detail.reason === 'previous' || detail.reason === 'auto-fallback') {
        navigate(step.navigateTo);
      }
    },
    [flatSteps, navigate, location.pathname]
  );

  const value = useMemo(
    () => ({
      startTutorial,
      exitTutorial,
      isRunning: !!currentTutorial,
    }),
    [startTutorial, exitTutorial, currentTutorial]
  );

  return (
    <TutorialContext.Provider value={value}>
      <AnnotationContext
        currentTutorial={currentTutorial}
        onStartTutorial={({ detail }) => setCurrentTutorial(detail.tutorial)}
        onExitTutorial={exitTutorial}
        onStepChange={handleStepChange}
        onFinish={exitTutorial}
        i18nStrings={I18N_STRINGS}
      >
        {children}
      </AnnotationContext>
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return ctx;
}
